import { Checkbox, DatePicker, Select } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import 'react-quill-new/dist/quill.snow.css';
import countries_en from 'world_countries_lists/data/countries/en/countries.json';
import countries_es from 'world_countries_lists/data/countries/es/countries.json';
import countries_pt from 'world_countries_lists/data/countries/pt/countries.json';

import useAPIQuery from '../../../api/useAPIQuery';
import Form from '../../../components/Form';
import i18n from '../../../i18n';
import { PublicationFormValues } from './PublicationForm';
import styles from './PublicationInfoForm.module.css';

interface PublicationInfoFormProps {
  linkedinExternalPublication: boolean;
  externalPublication: boolean;
  showWhatsAppSubscription?: boolean;
}
export const isDescriptionEmpty = (t: string | undefined) =>
  !t || t?.replaceAll('<br>', '') === '<p></p>';

const PublicationInfoForm = ({
  linkedinExternalPublication,
  externalPublication,
  showWhatsAppSubscription,
}: PublicationInfoFormProps) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<PublicationFormValues>();

  const [countries, setCountries] =
    useState<{ id: number; alpha2: string; alpha3: string; name: string }[]>(countries_pt);

  const countryValue = Form.useWatch('country', form);
  const stateValue = Form.useWatch('state', form);
  const externalPublicationValue = Form.useWatch('externalPublication', form);
  const googleForJobsExternalPublicationValue = Form.useWatch(
    'googleForJobsExternalPublication',
    form
  );
  const netvagasExternalPublicationValue = Form.useWatch('netvagasExternalPublication', form);
  const linkedinExternalPublicationValue = Form.useWatch('linkedinExternalPublication', form);
  const careerjetExternalPublicationValue = Form.useWatch('careerjetExternalPublication', form);
  const joobleExternalPublicationValue = Form.useWatch('joobleExternalPublication', form);

  const lang = i18n.language?.split('-')[0];

  useEffect(() => {
    const setCountriesWithLang = async () => {
      try {
        if (lang === 'en') {
          setCountries(countries_en);
        } else if (lang === 'es') {
          setCountries(countries_es);
        } else {
          setCountries(countries_pt);
        }
      } catch (error) {
        console.error('Failed setting countries JSON:', error);
      }
    };

    setCountriesWithLang();
  }, [lang]);

  const states = useAPIQuery(
    `/country-state-city/countries/${countryValue?.toUpperCase()}/states/`,
    {
      enabled: !!countryValue,
      refetchOnWindowFocus: false,
    }
  );

  const cities = useAPIQuery(
    `/country-state-city/countries/${countryValue?.toUpperCase()}/states/${stateValue}/cities/`,
    {
      enabled: !!countryValue && !!stateValue,
      refetchOnWindowFocus: false,
    }
  );

  return (
    <>
      <Form.Row>
        <Form.Col span={8}>
          <Form.Item
            shouldUpdate={(prevValues, nextValues) =>
              prevValues.linkedinExternalPublication !== nextValues.linkedinExternalPublication ||
              prevValues.careerjetExternalPublication !== nextValues.careerjetExternalPublication ||
              prevValues.joobleExternalPublication !== nextValues.joobleExternalPublication
            }
            noStyle
          >
            {(form) => (
              <Form.Item
                name="country"
                label={t('País')}
                required
                rules={[
                  {
                    required: true,
                    message: t('Por favor, insira o país da vaga'),
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  placeholder={t('País')}
                  onClear={() => form.setFieldsValue({ state: undefined, city: undefined })}
                  onChange={(value) =>
                    form.setFieldsValue({
                      country: value,
                      state: undefined,
                      city: undefined,
                    })
                  }
                  data-testid="select-country"
                >
                  {countries.map((country) => (
                    <Select.Option key={country.id} value={country.alpha2.toLocaleUpperCase()}>
                      {country.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Form.Item>
        </Form.Col>
        <Form.Col span={8}>
          <Form.Item shouldUpdate={(prev, next) => prev.country !== next.country}>
            {(form) => (
              <Form.Item
                label={t('Estado')}
                name="state"
                required={
                  linkedinExternalPublicationValue ||
                  googleForJobsExternalPublicationValue ||
                  netvagasExternalPublicationValue ||
                  careerjetExternalPublicationValue ||
                  joobleExternalPublicationValue
                }
                rules={[
                  {
                    required:
                      linkedinExternalPublicationValue ||
                      googleForJobsExternalPublicationValue ||
                      netvagasExternalPublicationValue ||
                      careerjetExternalPublicationValue ||
                      joobleExternalPublicationValue,
                    message: t('Por favor, insira o estado da vaga'),
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  placeholder={t('Estado')}
                  disabled={!countryValue}
                  onClear={() => form.setFieldsValue({ city: undefined })}
                  onChange={(value) => form.setFieldsValue({ state: value, city: undefined })}
                  data-testid="select-state"
                >
                  {states.data?.map((state) => (
                    <Select.Option key={state.isoCode} value={state.isoCode}>
                      {state.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Form.Item>
        </Form.Col>
        <Form.Col span={8}>
          <Form.Item
            label={t('Cidade')}
            name="city"
            required={
              linkedinExternalPublicationValue ||
              googleForJobsExternalPublicationValue ||
              netvagasExternalPublicationValue ||
              careerjetExternalPublicationValue ||
              joobleExternalPublicationValue
            }
            rules={[
              {
                required:
                  linkedinExternalPublicationValue ||
                  googleForJobsExternalPublicationValue ||
                  netvagasExternalPublicationValue ||
                  careerjetExternalPublicationValue ||
                  joobleExternalPublicationValue,
                message: t('Por favor, insira a cidade da vaga'),
              },
            ]}
          >
            <Select
              allowClear
              showSearch
              placeholder={t('Cidade')}
              disabled={!stateValue}
              data-testid="select-city"
            >
              {cities.data?.map((city) => (
                <Select.Option key={city.name} value={city.name}>
                  {city.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form.Col>
      </Form.Row>
      <Form.Row>
        <Form.Col span={12}>
          <Form.Item
            name="externalPublicationPeriod"
            label={t('Data de publicação')}
            required={externalPublicationValue}
            rules={[
              {
                required: externalPublicationValue,
                message: t('Campo obrigatório para publicação.'),
              },
              {
                validator: async (rule, value) => {
                  if (
                    value &&
                    moment(value[1]).startOf('day').isSameOrBefore(moment(value[0]).startOf('day'))
                  ) {
                    return Promise.reject(
                      new Error(t('A data de término deve ser posterior à data de início.'))
                    );
                  } else return Promise.resolve();
                },
              },
              {
                validator: async (rule, value) => {
                  const isLinkedinDirty = form.isFieldTouched('linkedinExternalPublication');
                  const isCareerjetDirty = form.isFieldTouched('careerjetExternalPublication');
                  const isJoobleDirty = form.isFieldTouched('joobleExternalPublication');
                  const isExternalPublicationDirty = form.isFieldTouched('externalPublication');

                  if (
                    !externalPublication &&
                    isExternalPublicationDirty &&
                    value &&
                    moment(value[1]).startOf('day').isSameOrBefore(moment().startOf('day'))
                  ) {
                    return Promise.reject(
                      new Error(
                        t('A data de término da publicação deve ser posterior ao dia de hoje.')
                      )
                    );
                  }

                  if (
                    !linkedinExternalPublication &&
                    isLinkedinDirty &&
                    value &&
                    moment(value[1]).startOf('day').isSameOrBefore(moment().startOf('day'))
                  ) {
                    return Promise.reject(
                      new Error(
                        t('A data de término da publicação deve ser posterior ao dia de hoje.')
                      )
                    );
                  }

                  if (
                    isCareerjetDirty &&
                    value &&
                    moment(value[1]).startOf('day').isSameOrBefore(moment().startOf('day'))
                  ) {
                    return Promise.reject(
                      new Error(
                        t('A data de término da publicação deve ser posterior ao dia de hoje.')
                      )
                    );
                  }

                  if (
                    isJoobleDirty &&
                    value &&
                    moment(value[1]).startOf('day').isSameOrBefore(moment().startOf('day'))
                  ) {
                    return Promise.reject(
                      new Error(
                        t('A data de término da publicação deve ser posterior ao dia de hoje.')
                      )
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <DatePicker.RangePicker
              data-testid="external-publish-date-picker"
              disabledDate={(current) => {
                // Days before today are not allowed
                return current && current < moment().startOf('day');
              }}
              format="L"
              className={styles.externalPublish}
            />
          </Form.Item>
        </Form.Col>
        <Form.Col span={12}>
          <Form.Item name="timezone" label={t('Fuso horário')}>
            <Select data-testid="select-gmt">
              <Select.Option value="America/Sao_Paulo">GMT (-03:00) Brasília</Select.Option>
              <Select.Option value="America/Rio_Branco">GMT (-05:00) Rio Branco</Select.Option>
              <Select.Option value="America/Manaus">GMT (-04:00) Manaus</Select.Option>
              <Select.Option value="America/Noronha">
                GMT (-02:00) Fernando de Noronha
              </Select.Option>
            </Select>
          </Form.Item>
        </Form.Col>
        {showWhatsAppSubscription && (
          <Form.Col span={24}>
            <Form.Item name="whatsAppSubscription" valuePropName="checked" noStyle>
              <Checkbox>
                <Trans>Aceitar inscrição por WhatsApp</Trans>
              </Checkbox>
            </Form.Item>
          </Form.Col>
        )}
      </Form.Row>
    </>
  );
};

export default PublicationInfoForm;
